import React from 'react';
import { useWallet } from '../../hooks/useWallet';
import { usePortfolio, ChainBalance } from '../../hooks/usePortfolio';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatAddress } from '../../utils/web3';

export const PortfolioPage: React.FC = () => {
  const { walletInfo } = useWallet();
  const { balances, isLoading, refreshBalances } = usePortfolio(walletInfo.address);

  // Format balance for display
  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    if (num === 0) return '0';
    if (num < 0.0001) return '<0.0001';
    return num.toFixed(4);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Portfolio</h1>
        <Button 
          onClick={refreshBalances} 
          isLoading={isLoading}
          size="sm"
          variant="outline"
        >
          Refresh Balances
        </Button>
      </div>

      {!walletInfo.isConnected ? (
        <Card title="Connect Wallet" className="text-center py-10">
          <p className="text-gray-400 mb-4">Please connect your wallet to view your portfolio</p>
        </Card>
      ) : isLoading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {balances.map((chainBalance) => (
            <ChainBalanceCard key={chainBalance.chainId} chainBalance={chainBalance} />
          ))}
        </div>
      )}
    </div>
  );
};

const ChainBalanceCard: React.FC<{ chainBalance: ChainBalance }> = ({ chainBalance }) => {
  const hasTokens = Object.keys(chainBalance.tokenBalances).length > 0;
  const hasNonZeroBalance = 
    parseFloat(chainBalance.nativeBalance) > 0 || 
    Object.values(chainBalance.tokenBalances).some(token => parseFloat(token?.balance || '0') > 0);

  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    if (num === 0) return '0';
    if (num < 0.0001) return '<0.0001';
    return num.toFixed(4);
  };

  return (
    <Card 
      title={
        <div className="flex items-center space-x-2">
          {chainBalance.chainIcon && (
            <img src={chainBalance.chainIcon} alt={chainBalance.chainName} className="w-6 h-6 rounded-full" />
          )}
          <span>{chainBalance.chainName}</span>
        </div>
      }
      className={`${!hasNonZeroBalance ? 'opacity-70' : ''}`}
    >
      {chainBalance.isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      ) : chainBalance.error ? (
        <div className="text-red-500 py-2 text-sm">
          Error: {chainBalance.error}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full overflow-hidden">
                <img src={chainBalance.nativeIcon} alt={chainBalance.nativeSymbol} className="w-full h-full object-cover" />
              </div>
              <span>{chainBalance.nativeSymbol}</span>
            </div>
            <span className="font-medium">
              {formatBalance(chainBalance.nativeBalance)}
            </span>
          </div>

          {hasTokens &&
            Object.entries(chainBalance.tokenBalances).map(([tokenSymbol, tokenData]) => (
              tokenData && (
                <div key={tokenSymbol} className="flex justify-between items-center py-2 border-b border-gray-700/50">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img src={tokenData.icon} alt={tokenData.symbol} className="w-full h-full object-cover" />
                    </div>
                    <span>{tokenData.symbol}</span>
                  </div>
                  <span className="font-medium">
                    {formatBalance(tokenData.balance)}
                  </span>
                </div>
              )
            ))
          }
        </div>
      )}
    </Card>
  );
};
